import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Building2, Save, BadgeCheck, ExternalLink, MapPin } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

export default function MyCentre() {
  const [centres, setCentres] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { const { data } = await api.get("/my/centres"); setCentres(data.centres); }
    catch (e) { toast.error(apiErr(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); document.title = "My Centre | TaperPath"; return () => { document.title = "TaperPath"; }; }, []);

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <div>
        <h1 className="font-head text-3xl font-bold text-[#2C363F] sm:text-4xl">My Centre</h1>
        <p className="mt-2 text-muted-foreground">Keep your listing accurate. Changes go live straight away.</p>
      </div>

      {loading ? (
        <div className="h-40 animate-pulse rounded-2xl bg-muted" />
      ) : centres.length === 0 ? (
        <div data-testid="no-centres" className="rounded-2xl border border-dashed border-border bg-white p-12 text-center">
          <Building2 className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mt-3 font-head font-semibold text-[#2C363F]">You don't manage any listings yet</p>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">Find your service in the directory and claim it. Once our team verifies your claim, it'll appear here for you to manage.</p>
          <Link to="/app/locator" data-testid="go-locator" className="mt-4 inline-flex rounded-full bg-[#4A7c59] px-5 py-2.5 text-sm font-semibold text-white">Find my centre</Link>
        </div>
      ) : (
        <div className="space-y-6">
          {centres.map((c) => <CentreEditor key={c.id} centre={c} onSaved={load} />)}
        </div>
      )}
    </div>
  );
}

function CentreEditor({ centre, onSaved }) {
  const [f, setF] = useState({
    description: centre.description || "", phone: centre.phone || "", website: centre.website || "",
    hours: centre.hours || "", address: centre.address || "", city: centre.city || "", postcode: centre.postcode || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try { await api.put(`/centres/${centre.id}`, f); toast.success("Listing updated — changes are live."); onSaved?.(); }
    catch (e) { toast.error(apiErr(e)); }
    setSaving(false);
  };

  return (
    <div data-testid={`centre-editor-${centre.id}`} className="rounded-2xl border border-border bg-white p-6 shadow-sm sm:p-8">
      <div className="flex flex-wrap items-center gap-2">
        <Building2 className="h-5 w-5 text-[#4A7c59]" />
        <h2 className="font-head text-xl font-semibold text-[#2C363F]">{centre.name}</h2>
        {centre.verified && <span className="flex items-center gap-1 rounded-full bg-[#4A7c59]/10 px-2 py-0.5 text-xs font-medium text-[#4A7c59]"><BadgeCheck className="h-3.5 w-3.5" /> Verified</span>}
      </div>
      <div className="mt-5 space-y-4">
        <div>
          <Label>Description</Label>
          <Textarea data-testid="edit-description" value={f.description} onChange={set("description")} className="mt-1 rounded-xl" rows={3} placeholder="Tell people what your service offers" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><Label>Phone</Label><Input data-testid="edit-phone" value={f.phone} onChange={set("phone")} className="mt-1 rounded-xl py-6" /></div>
          <div><Label>Website</Label><Input data-testid="edit-website" value={f.website} onChange={set("website")} className="mt-1 rounded-xl py-6" placeholder="https://" /></div>
        </div>
        <div><Label>Opening hours</Label><Input data-testid="edit-hours" value={f.hours} onChange={set("hours")} className="mt-1 rounded-xl py-6" placeholder="e.g. Mon–Fri 9am–5pm" /></div>
        <div className="rounded-xl bg-muted/50 p-4">
          <div className="flex items-center gap-2 text-sm font-medium text-[#2C363F]"><MapPin className="h-4 w-4 text-muted-foreground" /> Location</div>
          <p className="mt-1 text-xs text-muted-foreground">Updating address or postcode re-maps your pin.</p>
          <div className="mt-3 space-y-3">
            <Input data-testid="edit-address" value={f.address} onChange={set("address")} className="rounded-xl py-6" placeholder="Address" />
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="edit-city" value={f.city} onChange={set("city")} className="rounded-xl py-6" placeholder="Town/City" />
              <Input data-testid="edit-postcode" value={f.postcode} onChange={set("postcode")} className="rounded-xl py-6" placeholder="Postcode" />
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button data-testid={`save-centre-${centre.id}`} onClick={save} disabled={saving} className="rounded-full bg-[#4A7c59] hover:bg-[#3B6347]"><Save className="mr-2 h-4 w-4" /> {saving ? "Saving…" : "Save changes"}</Button>
          <Link to="/app/locator" className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-medium text-[#4A7c59]"><ExternalLink className="h-4 w-4" /> View in directory</Link>
        </div>
      </div>
    </div>
  );
}
